<script setup lang="ts">
import type { Property } from '~/types/properties';
import { CANONICAL_SALE_STATUS } from '~/constants/properties';
import { projectStageLabel } from '~/utils/properties/projectStage';
import { COMING_SOON_PLACEHOLDER } from '~/utils/comingSoonImages';
import { normalizeDeveloperName } from '~/utils/developerName';
import { useDeveloperLogosQuery } from '~/composables/useDeveloperLogosQuery';
import { isVideoUrl } from '~/utils/mediaType';
import { convertCurrency } from '@/helpers/currency.helper';
import { exclusiveCardCopy, formatExclusiveEarnings } from '~/utils/exclusives/exclusiveCardCopy';

// Compact project card — image-led, pills overlaid on art, tight text stack,
// no action buttons. Whole card is the click target to the property detail.
const {
  property,
  isFavorite = false,
  isLoading = false,
  isHighlighted = false,
  heroOverride = null,
  heroPortrait = false,
  openSource = null,
} = defineProps<{
  property: Property;
  isFavorite?: boolean;
  isLoading?: boolean;
  // When true (a map marker for this project was clicked), the card gets a
  // gold ring so the user can spot it after the list scrolls to it.
  isHighlighted?: boolean;
  // Server-resolved landscape hero (see useLandscapeHeroes). When set it
  // replaces the project's default photo so the card never leads with a
  // cropped vertical image.
  heroOverride?: string | null;
  // True when `heroOverride` is the project's only (portrait) image — the hero
  // is smart-cropped to landscape instead of CSS centre-cropped.
  heroPortrait?: boolean;
  // Where the card sits on /explore; set → opening a 100% Commission project is tracked (ID-118).
  openSource?: 'filtered_list' | 'list' | 'map' | null;
}>();

const emit = defineEmits<{
  (e: 'toggleFavorite'): void;
  // Hover → highlight this project's pin on the explore map (and clear on
  // leave). Emits the complex_id used as the marker key.
  (e: 'hover', complexId: string | null): void;
}>();

const { formatFromAed, displayCurrency } = useAedMoneyDisplay();
const { t } = useI18n();
const { gate } = useAccessGate();
const { sendEventToBackend } = useTracking();

// ── Hero image ──────────────────────────────────────────────────────────
// Priority: VIDEO-FIRST → hero_selected still → first still.
//
// `property.photo` is already the hero_selected still (or first still) —
// mapToPropertyShape sets it from image_meta.hero_selected. The /explore
// `heroOverride` (server landscape probe) replaces it for wide-card contexts
// and is kept as the still source when present.
//
// Video takes unconditional precedence: when a project has a walkthrough .mp4
// in gallery[], the card leads with the video (autoplay-muted loop). The
// still image is the fallback when there is no video.
const cardVideo = computed<string | null>(() => {
  const gallery = (property as unknown as { gallery?: string[] }).gallery;
  if (!Array.isArray(gallery)) return null;
  return gallery.find((u) => typeof u === 'string' && isVideoUrl(u)) ?? null;
});

// Off-screen walkthroughs must not stream (ID-165): /explore renders ~20 cards and each .mp4 is MBs, which starved
// the map's script. The src is bound once the card first enters the viewport; playback follows visibility.
const videoEl = ref<HTMLVideoElement | null>(null);
const isVideoVisible = ref(false);
const hasVideoEnteredView = ref(false);
useIntersectionObserver(videoEl, ([entry]) => {
  isVideoVisible.value = !!entry?.isIntersecting;
  if (isVideoVisible.value) hasVideoEnteredView.value = true;
});
watch(isVideoVisible, (visible) => {
  if (visible) videoEl.value?.play().catch(() => undefined);
  else videoEl.value?.pause();
});

// Still hero: prefer the /explore landscape override, else `photo` (which is
// already hero_selected-aware from the server mapper).
const heroSource = computed(() => heroOverride || property.photo);

const { url: cdnUrl } = useComingSoonImage(heroSource);

const hasCoverImage = computed(() => !!heroSource.value && cdnUrl.value !== COMING_SOON_PLACEHOLDER);

// ── Status pill (top-left, first) ─────────────────────────────────
const canonicalStatusLabel = computed(() => projectStageLabel(property));

const statusPillLabel = computed(() => {
  switch (canonicalStatusLabel.value) {
    case CANONICAL_SALE_STATUS.PRE_LAUNCH:
      return t('listings.projectCard.statusPill.presale');
    case CANONICAL_SALE_STATUS.EOI_STAGE:
      return t('listings.projectCard.statusPill.earlyAccess');
    case CANONICAL_SALE_STATUS.ON_SALE:
      return t('listings.projectCard.statusPill.onSale');
    case CANONICAL_SALE_STATUS.SOLD_OUT:
      return t('listings.projectCard.statusPill.soldOut');
    default:
      return null;
  }
});

// ── Developer avatar (bottom-left of image) ───────────────────────
const developerColorClass = computed(() => {
  const name = (property.developer?.en || '').toLowerCase();
  if (name.includes('emaar')) return 'bg-developer-emaar';
  if (name.includes('damac')) return 'bg-developer-damac';
  if (name.includes('sobha')) return 'bg-developer-sobha';
  if (name.includes('meraas')) return 'bg-developer-meraas';
  if (name.includes('nakheel')) return 'bg-developer-nakheel';
  if (name.includes('aldar')) return 'bg-developer-aldar';
  return 'bg-black-90';
});

const developerInitial = computed(() => (property.developer?.en?.[0] || '?').toUpperCase());

// Bot-DB developer logo lookup — falls back to the upstream Geniemap
// `property.logo` when present, then to the colored-letter avatar.
const { data: devLogos } = useDeveloperLogosQuery();

const resolvedLogoSrc = computed<string | null>(() => {
  const map = devLogos.value;
  const name = property.developer?.en;
  if (map && name) {
    const key = normalizeDeveloperName(name);
    if (key && map[key]) return map[key];
  }
  return property.logo || null;
});

const hasDeveloperLogo = computed(() => !!resolvedLogoSrc.value);

// ── Subtitle: "<location> · by <developer>" ──────────────────────
const subtitleLine = computed(() => {
  const dev = property.developer?.en;
  const parts: string[] = [];
  if (property.address) parts.push(property.address);
  if (dev) parts.push(t('listings.projectCard.byDeveloper', { developer: dev }));
  return parts.join(' · ');
});

// ── Bottom stat row ───────────────────────────────────────────────
const fromValue = computed(() => {
  const n = Number(property.price?.min);
  if (!n) return '—';
  return formatFromAed(n);
});

const handoverValue = computed(() => {
  const raw = property as unknown as {
    handover_year?: number | string;
    predicted_completion_at?: string;
  };
  if (raw.handover_year) return String(raw.handover_year);
  if (raw.predicted_completion_at) {
    const year = new Date(raw.predicted_completion_at).getUTCFullYear();
    return Number.isFinite(year) ? String(year) : '—';
  }
  return '—';
});

// ── 100% Commission (ID-118): label, units, earnings (or the commission gate), "New on Insiders" ──
const exclusiveCopy = computed(() => exclusiveCardCopy(property.exclusive));

const exclusiveUnitsText = computed(() => {
  const units = exclusiveCopy.value?.units;
  if (!units) return '';
  return units.key === 'exclusives.card.unitsMany' ? t(units.key, { count: units.count }) : t(units.key);
});

const exclusiveEarningsText = computed(() => {
  const earnings = exclusiveCopy.value?.earnings;
  if (!earnings) return '';
  const currency = displayCurrency.value;
  const preferred =
    currency && currency !== 'AED' ? { currency, amount: convertCurrency(earnings.amountAed, 'AED', currency) } : null;
  const amount = formatExclusiveEarnings(earnings.amountAed, preferred);
  if (earnings.payoutSlaHours === null) return t('exclusives.card.earningsNoFastPay', { amount });
  return t('exclusives.card.earnings', { amount, hours: earnings.payoutSlaHours });
});

const onCommissionGate = (e: MouseEvent) => {
  e.stopPropagation();
  e.preventDefault();
  gate('commission', undefined, { projectTitle: property.title?.en });
};

const onOpen = () => {
  const block = property.exclusive;
  if (!openSource || !block?.isExclusive) return;
  void sendEventToBackend('exclusive_project_opened', {
    catalogueProjectId: block.catalogueProjectId,
    source: openSource,
  });
};

const onFavorite = (e: MouseEvent) => {
  e.stopPropagation();
  e.preventDefault();
  emit('toggleFavorite');
};

// Prefer SEO slug URL when the project shape carries both slug + developerSlug
// (populated by mapToPropertyShape on the server). Falls back to the legacy
// /properties/<id> for shapes that didn't ship slugs — the
// property-id-to-slug-redirect middleware 301s those to the canonical URL.
const detailRoute = computed(() => {
  const p = property as { complex_id: string; slug?: string; developerSlug?: string };
  return p.slug && p.developerSlug ? `/projects/${p.developerSlug}/${p.slug}` : `/properties/${p.complex_id}`;
});
</script>

<template>
  <NuxtLink
    :to="detailRoute"
    class="group block"
    @click="onOpen"
    @mouseenter="emit('hover', String(property.complex_id ?? property.id ?? ''))"
    @mouseleave="emit('hover', null)"
  >
    <article
      class="flex h-full flex-col overflow-hidden rounded-2xl border bg-white transition-shadow duration-150 hover:shadow-[0_4px_18px_rgba(13,15,21,0.06)]"
      :class="
        isHighlighted
          ? 'border-gold-primary-100 ring-2 ring-gold-primary-100 shadow-[0_4px_22px_rgba(255,182,72,0.3)]'
          : 'border-black/[0.06]'
      "
    >
      <!-- Art / hero — fixed height + absolute image so portrait sources can't bust the box -->
      <!-- Priority: VIDEO > hero_selected still (property.photo) > first still > placeholder -->
      <div class="relative h-[180px] shrink-0 overflow-hidden bg-black/[0.05]">
        <!-- VIDEO-FIRST: autoplay-muted loop when a walkthrough video exists -->
        <video
          v-if="cardVideo"
          ref="videoEl"
          :src="hasVideoEnteredView ? cardVideo : undefined"
          muted
          loop
          playsinline
          autoplay
          preload="none"
          class="absolute inset-0 size-full object-cover"
        />
        <!-- Still hero (hero_selected or first still): portrait blur + contain fallback -->
        <template v-else-if="hasCoverImage">
          <!-- Portrait-only fallback: blurred fill behind the contained image so
               the whole vertical photo shows without an ugly centre-crop. -->
          <NuxtImg
            v-if="heroPortrait"
            :src="cdnUrl"
            loading="lazy"
            aria-hidden="true"
            class="absolute inset-0 size-full scale-110 object-cover opacity-60 blur-xl"
          />
          <NuxtImg
            :src="cdnUrl"
            loading="lazy"
            :placeholder="COMING_SOON_PLACEHOLDER"
            :class="['absolute inset-0 size-full', heroPortrait ? 'object-contain' : 'object-cover']"
          />
        </template>
        <PropertiesUpcomingPlaceholder v-else />

        <!-- Top-left pill: status only. The handover quarter lives in the
             bottom stat row (Price from | Handover) — we don't repeat it as an
             overlay on the image. -->
        <div
          v-if="statusPillLabel || exclusiveCopy"
          class="absolute start-3 top-3 flex gap-1.5"
        >
          <span
            v-if="statusPillLabel"
            class="rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-semibold text-[#0d0f15] backdrop-blur-sm"
          >
            {{ statusPillLabel }}
          </span>
          <span
            v-if="exclusiveCopy"
            class="rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-semibold text-[#0d0f15] backdrop-blur-sm"
          >
            {{ t('exclusives.card.label') }}
          </span>
        </div>

        <!-- Top-right bookmark -->
        <button
          type="button"
          class="absolute end-3 top-3 grid size-8 place-items-center rounded-full bg-white/95 text-black-60 backdrop-blur-sm transition-colors hover:text-black-100 disabled:opacity-60"
          :aria-label="t('common.actions.save')"
          :disabled="isLoading"
          @click="onFavorite"
        >
          <span
            v-if="isLoading"
            class="size-3.5 animate-spin rounded-full border-2 border-black-40 border-t-transparent"
          />
          <svg
            v-else-if="isFavorite"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="currentColor"
            class="text-gold-primary-120"
            aria-hidden="true"
          >
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
          </svg>
          <svg
            v-else
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
          </svg>
        </button>

        <!-- Bottom-left developer avatar (logo if present, else colored initial) -->
        <div
          v-if="hasDeveloperLogo"
          class="absolute bottom-3 start-3"
        >
          <NuxtImg
            :src="resolvedLogoSrc!"
            loading="lazy"
            alt=""
            class="h-8 w-auto max-w-[140px] object-contain"
            style="filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5))"
          />
        </div>
        <div
          v-else-if="property.developer?.en"
          class="absolute bottom-3 start-3 grid size-9 place-items-center rounded-lg text-[12px] font-bold text-white shadow-sm"
          :class="developerColorClass"
        >
          {{ developerInitial }}
        </div>
      </div>

      <!-- Body -->
      <div class="flex flex-1 flex-col px-4 pb-4 pt-3">
        <h3 class="truncate font-serif text-[18px] font-semibold leading-tight tracking-[-0.015em] text-[#0d0f15]">
          {{ property.title.en }}
        </h3>
        <div
          v-if="subtitleLine"
          class="mt-1 truncate text-[13px] text-black-60"
        >
          {{ subtitleLine }}
        </div>

        <!-- Stat row: Price from | Handover -->
        <div class="mt-3 flex items-end justify-between gap-3 border-t border-black/[0.05] pt-3">
          <div class="flex flex-col gap-0.5">
            <span class="font-mono text-[9px] font-semibold uppercase tracking-[0.08em] text-black-60">
              {{ t('listings.projectCard.priceFrom') }}
            </span>
            <span class="font-serif text-[15px] font-semibold tabular-nums text-black-100">
              {{ fromValue }}
            </span>
          </div>
          <div class="flex flex-col items-end gap-0.5">
            <span class="font-mono text-[9px] font-semibold uppercase tracking-[0.08em] text-black-60">
              {{ t('listings.projectCard.handover') }}
            </span>
            <span class="font-serif text-[15px] font-semibold tabular-nums text-black-100">
              {{ handoverValue }}
            </span>
          </div>
        </div>

        <div
          v-if="exclusiveCopy"
          class="mt-2 flex flex-col gap-0.5 text-[12px] leading-snug"
        >
          <span
            v-if="exclusiveEarningsText"
            class="font-semibold text-black-100"
          >
            {{ exclusiveEarningsText }}
          </span>
          <button
            v-else-if="exclusiveCopy.showCommissionGate"
            type="button"
            class="self-start font-semibold text-black-80 underline underline-offset-2 hover:text-black-100"
            @click="onCommissionGate"
          >
            {{ t('property.access.shared.verifyThirtySec') }}
            <span class="sr-only">{{ t('exclusives.card.earningsGateAriaLabel') }}</span>
          </button>
          <span class="text-black-60">{{ exclusiveUnitsText }}</span>
          <span
            v-if="exclusiveCopy.isNew"
            class="text-black-60"
          >
            {{ t('exclusives.card.isNew') }}
          </span>
        </div>
      </div>
    </article>
  </NuxtLink>
</template>
